import React, { useCallback, useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";

import {
  getProductionsData,
  getAssetKpis,
} from "service/productionData";

import {
  toDateSeries,
  computeAcummulativeData,
  computeNormalCurve,
} from "./DataFunctions";
import { getDateBefore } from "utils/dateUtil";
import { DataPeriodOptions } from "app/codes";
import { ViewersProductOptions } from "../../app/codes";

const CUMULATIVE_PRODUCTS = [
  "oil_prd_rate",
  "gas_prd_rate",
  "water_prd_rate",
  "power_consumption",
];

// --- AV-A4 petroleum unit constants (口径已定，直接用) -----------------------
// gas series (*_prd_rate) carry Mscf; GOR = qg/qo is therefore in Mscf/bbl.
// Industry-standard GOR is scf/bbl => multiply by 1000.
const MSCF_TO_SCF = 1000;
// BOE: 6 Mscf of gas == 1 bbl of oil equivalent.
const MSCF_PER_BOE = 6;
// Approx. days/month used to turn a monthly volume into a daily rate for the
// AV-A5 "latest daily production" first-screen cards (client-side derivation).
const DAYS_PER_MONTH = 30.4375;

const IndicatorIcon = {
  up: "/static/icons/indicator-up.svg",
  down: "/static/icons/indicator-down.svg",
};

function commarize(num) {
  if (num == null || !isFinite(num)) {
    return num === Infinity ? "∞" : "—";
  }
  return num.toLocaleString();
}

function setDisplayValue(value, decimalNum) {
  if (value == null || value === 0) {
    return value;
  }

  return value.toFixed(decimalNum);
}

// AV-A6: build a rate-vs-time sparkline ([date, value] pairs) from a monthly
// rate series, so the cumulative KPI cards show DECLINE instead of the
// monotone-increasing cumulative curve that masks it.
function rateSparkline(series, startDate, endDate) {
  let points = computeNormalCurve(series, startDate, endDate);
  if (points == null) {
    return [];
  }
  return points.map((item) => [item.date, item.value]);
}

// AV-A5: get the last entry that actually carries a (non-null) value, i.e. the
// most recent VALID production month, so latest-rate cards never read an empty
// trailing month (data lags 1-3 months). Returns { date, value } or null.
function getLatestValidEntry(series) {
  if (!series || series.length === 0) {
    return null;
  }
  for (let i = series.length - 1; i >= 0; i--) {
    if (series[i] && series[i].value != null) {
      return series[i];
    }
  }
  return null;
}

function SummaryCard(props) {
  const {
    icon,
    color,
    title,
    dataSeries,
    getDates,
    computeFunc,
    computeCurve,
    unit,
    // AV-A4: optional formatter so each card renders its value in the right
    // unit (GOR ×1000 scf/bbl, Water Cut ×100 %, BOE, etc.). Defaults to the
    // existing commarize() behaviour when not supplied.
    valueFormatter,
    // AV-A6: clarifies the % semantics ("本期产量 vs 上期产量" for cum cards).
    percentLabel,
  } = props;

  const [value, setValue] = useState(0);
  const [percent, setPercent] = useState(0);
  const [period, setPeriod] = useState(0);
  const [yMinValue, setYMinValue] = useState(0);
  const [yMaxValue, setYMaxValue] = useState(0);
  const [lineSeries, setLineSeries] = useState([]);

  const bgColor = {
    background: color,
  };

  const computeCurveData= useCallback((series, startDate, endDate) => {
    let curve = computeCurve(series, startDate, endDate);

    if (curve && curve.length > 0) {
      let values = curve.map((item) => {
        return item.value;
      });
      setYMinValue(Math.min(...values));
      setYMaxValue(Math.max(...values));
    }

    let line = {
      type: "line",
      smooth: true,
      symbol: "none",
      emphasis: { focus: "series" },
      lineStyle: {
        color: "#fff",
        width: 2,
      },
      data: curve,
    };
    setLineSeries([line]);
  }, [computeCurve]);

  const computeValues = useCallback((days) => {
    // AV-A2: anchor the rolling window to the LAST date that actually has data
    // (production data lags 1-3 months) instead of today's date. Anchoring to
    // new Date() makes "last 30 days" land in an empty interval -> KPI shows 0
    // / a false drop. last_data_date is derived client-side from the series
    // already loaded (max date across all product series); no BE dependency.
    let dates = getDates(dataSeries);
    let endDate =
      dates && dates.length > 0 ? dates[dates.length - 1] : new Date();
    let startDate = getDateBefore(endDate, days);
    let value2 = computeFunc(dataSeries, startDate, endDate);

    let value1 = computeFunc(
      dataSeries,
      getDateBefore(startDate, days + 1),
      getDateBefore(startDate, 1)
    );

    setValue(value2);
    // Guard against Infinity/NaN (e.g. GOR when oil=0) so the % stays sane.
    if (value1 !== 0 && isFinite(value1) && isFinite(value2)) {
      setPercent((value2 - value1) / value1);
    } else {
      setPercent(0);
    }

    computeCurveData(dataSeries, startDate, endDate);
  }, [computeCurveData, computeFunc, dataSeries, getDates]);

  useEffect(() => {
    if (dataSeries === null || Object.keys(dataSeries).length === 0) {
      return;
    }
    let dates = getDates(dataSeries);
    if (period === 0) {
      let value1 = computeFunc(dataSeries, dates[0], dates[dates.length - 1]);
      setValue(value1);
      computeCurveData(dataSeries, dates[0], dates[dates.length - 1]);
    } else if (period === 1) {
      // last week
      computeValues(7);
    } else if (period === 2) {
      // last month
      computeValues(30);
    } else if (period === 3) {
      // last year
      computeValues(365);
    }
  }, [dataSeries, period, getDates, computeCurveData, computeFunc, computeValues]);
  
  function getChartOptions() {
    let options = {
      grid: {
        left: "0",
        right: "0",
        bottom: "0",
        top: "0",
      },
      xAxis: {
        type: "time",
        // min:xMinValue,
        // max:xMaxValue,
        // min: 'dataMin',
        // max: 'dataMax',
      },
      yAxis: {
        type: "value",
        min: yMinValue,
        max: yMaxValue,
        splitLine: {
          show: false,
        },
      },
      series: (dataSeries && Object.keys(dataSeries).length > 0) ? lineSeries : [],
    };
    return options;
  }

  function changePeriod(period) {
    setPeriod(period);
  }

  return (
    <div
      className="d-flex justify-content-between align-items-stretch summary-card-container"
      style={bgColor}
    >
      <div className="d-flex align-items-center">
        <img src={icon} className="summary-card-icon" alt="" />
        <div className="d-flex flex-column summary-card-info">
          <div className="summary-card-title">
            <span>{title + (unit && ' (' + unit + ')')}</span>
          </div>
          <div className="summary-card-value">
            <span>
              {dataSeries && Object.keys(dataSeries).length > 0
                ? valueFormatter
                  ? valueFormatter(value)
                  : commarize(value)
                : 0}
            </span>
          </div>
          <div className="d-flex flex-row align-items-center">
            <img
              src={percent < 0 ? IndicatorIcon.down : IndicatorIcon.up}
              className="indicator-icon"
              alt=""
            />
            {/* AV-A6: percent is a fraction (curr vs prev); render as a real %
                (×100) and label the comparison so it isn't read as "vs today". */}
            <div className="summary-card-percent">
              <span>{setDisplayValue(percent * 100, 1)}%</span>
            </div>
            <div className="summary-card-desc">
              <span>
                {period !== 0
                  ? `${percentLabel || "本期 vs 上期"} · ${DataPeriodOptions[period].desc}`
                  : percentLabel || "本期 vs 上期"}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-end align-items-stretch flex-grow-1 ps-1">
        <div
          className="d-flex me-2 py-2"
          style={{ height: "80px", width: "160px" }}
        >
          <ReactECharts
            style={{ height: "100%", width: "100%" }}
            notMerge={true}
            option={getChartOptions()}
          />
        </div>
      </div>
      {/* <img src={trend_icon} className="summary-card-trend me-2" alt="" /> */}
      <div className="dropdown">
        <div className="summary-setting-btn" data-bs-toggle="dropdown">
          <i className="summary-setting-icon" />
        </div>
        <ul className="dropdown-menu">
          <li>
            <div
              className="dropdown-item"
              onClick={(e) => {
                changePeriod(0);
              }}
            >
              {DataPeriodOptions[0].name}
            </div>
          </li>
          <li>
            <div
              className="dropdown-item"
              onClick={(e) => {
                changePeriod(1);
              }}
            >
              {DataPeriodOptions[1].name}
            </div>
          </li>
          <li>
            <div
              className="dropdown-item"
              onClick={(e) => {
                changePeriod(2);
              }}
            >
              {DataPeriodOptions[2].name}
            </div>
          </li>
          <li>
            <div
              className="dropdown-item"
              onClick={(e) => {
                changePeriod(3);
              }}
            >
              {DataPeriodOptions[3].name}
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
}

// --- AV-A5: current-production first-screen ----------------------------------
// One compact card row. Now wired to GET /api/assets/<id>/kpis: latest daily
// rate per phase (oil bbl/d, gas Mscf/d, water bbl/d) comes from the endpoint's
// latest_daily_rate (a per-well sum over the most recent VALID month — more
// accurate than the client-side monthly-volume / days-per-month derivation,
// which is kept as a fallback when the endpoint value is missing). Active-wells
// (producing-status count) and Uptime (open-rate %) cannot be derived from the
// asset-level summed series at all, so they read straight from the endpoint.
function StatCard({ title, value, unit, note, color }) {
  return (
    <div
      className="d-flex flex-column justify-content-center summary-card-container"
      style={{ background: color, minHeight: "92px" }}
    >
      <div className="summary-card-title">
        <span>{title + (unit ? " (" + unit + ")" : "")}</span>
      </div>
      <div className="summary-card-value">
        <span>{value}</span>
      </div>
      {note && (
        <div className="summary-card-desc">
          <span>{note}</span>
        </div>
      )}
    </div>
  );
}

function latestDailyRate(series) {
  const entry = getLatestValidEntry(series);
  if (entry == null || entry.value == null) {
    return null;
  }
  // *_prd_rate carries a monthly volume; convert to an average daily rate.
  return entry.value / DAYS_PER_MONTH;
}

function fmtRate(v) {
  if (v == null || !isFinite(v)) return "—";
  return commarize(Math.round(v));
}

// AV-A5: pull a phase's daily rate from the kpis endpoint payload, falling back
// to the client-side monthly-volume / days-per-month derivation when the
// endpoint has no value (e.g. dict element missing). Returns { value, date }.
function endpointDailyRate(kpis, phase, fallbackSeries) {
  const node = kpis && kpis.latest_daily_rate && kpis.latest_daily_rate[phase];
  if (node && node.value != null && isFinite(node.value)) {
    return { value: node.value, date: node.date || null };
  }
  const v = fallbackSeries ? latestDailyRate(fallbackSeries) : null;
  const entry = fallbackSeries ? getLatestValidEntry(fallbackSeries) : null;
  return {
    value: v,
    date: entry && entry.date ? entry.date.toISOString().slice(0, 7) : null,
  };
}

function CurrentProductionRow({ dataSeries, kpis }) {
  const ready = dataSeries && Object.keys(dataSeries).length > 0;

  const oil = endpointDailyRate(
    kpis,
    "oil",
    ready ? dataSeries["oil_prd_rate"] : null
  );
  const gas = endpointDailyRate(
    kpis,
    "gas",
    ready ? dataSeries["gas_prd_rate"] : null
  );
  const water = endpointDailyRate(
    kpis,
    "water",
    ready ? dataSeries["water_prd_rate"] : null
  );

  // Prefer the endpoint's per-phase date; else the client-side latest valid
  // month; else the asset-wide last_data_date.
  const asOfFor = (node) => {
    const d = node && node.date ? String(node.date).slice(0, 7) : null;
    if (d) return `最近有效月 ${d}`;
    if (kpis && kpis.last_data_date)
      return `最近有效月 ${String(kpis.last_data_date).slice(0, 7)}`;
    return "待数据";
  };

  const activeWells =
    kpis && kpis.active_wells != null
      ? kpis.total_wells != null
        ? `${kpis.active_wells} / ${kpis.total_wells}`
        : commarize(kpis.active_wells)
      : "—";
  const uptime =
    kpis && kpis.uptime_pct != null ? kpis.uptime_pct.toFixed(1) : "—";

  return (
    <div className="row g-0">
      <div className="col pe-1">
        <StatCard
          title="Latest Oil"
          unit="bbl/d"
          value={fmtRate(oil.value)}
          note={asOfFor(oil)}
          color="rgba(0, 128, 0, 0.12)"
        />
      </div>
      <div className="col ps-1 pe-1">
        <StatCard
          title="Latest Gas"
          unit="Mscf/d"
          value={fmtRate(gas.value)}
          note={asOfFor(gas)}
          color="rgba(220, 0, 0, 0.10)"
        />
      </div>
      <div className="col ps-1 pe-1">
        <StatCard
          title="Latest Water"
          unit="bbl/d"
          value={fmtRate(water.value)}
          note={asOfFor(water)}
          color="rgba(0, 90, 220, 0.10)"
        />
      </div>
      <div className="col ps-1 pe-1">
        <StatCard
          title="Active Wells"
          unit=""
          value={activeWells}
          note="producing 口径"
          color="rgba(0, 128, 0, 0.08)"
        />
      </div>
      <div className="col ps-1">
        <StatCard
          title="Uptime"
          unit="%"
          value={uptime}
          note="开井率 (最近月 dayson)"
          color="rgba(0, 90, 220, 0.08)"
        />
      </div>
    </div>
  );
}

function AssetSummary(props) {
  const { wellId, assetId } = props;
  const { loadingIconActions } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];

  const [historicalData, setHistoricalData] = useState(null);
  // AV-A5: current-production KPIs (active wells / uptime / latest daily rate)
  // from GET /api/assets/<id>/kpis. Asset-scoped only (no per-well endpoint).
  const [kpis, setKpis] = useState(null);

  const updateCummulativeData = useCallback((data) => {
    let productionHistoricalData = {};
    for (const product of Object.keys(data)) {
      productionHistoricalData[product] = toDateSeries(data[product]);
    }
    setHistoricalData(productionHistoricalData);
  }, []);

  const refreshData = useCallback((uwi, assetId) => {
    let apiKeys = null;
    if (uwi != null) {
      apiKeys = [uwi];
    } else if (assetId == null) {
      return;
    }

    showLoadingIcon && showLoadingIcon();
    getProductionsData(
      apiKeys,
      assetId,
      CUMULATIVE_PRODUCTS,
      null,
      null,
      true,
      (tempData) => {
        updateCummulativeData(tempData);
        hideLoadingIcon && hideLoadingIcon();
      },
      (error) => {
        updateCummulativeData({});
        hideLoadingIcon && hideLoadingIcon();
      }
    );
  }, [updateCummulativeData, hideLoadingIcon, showLoadingIcon]);

  useEffect(() => {
    if (wellId == null && assetId == null) {
      return;
    }
    refreshData(wellId, assetId);
  }, [wellId, assetId, refreshData]);

  // AV-A5: fetch the current-production KPIs whenever the asset changes. This is
  // an asset-level endpoint; the per-well (wellId) drill-down has no KPI source,
  // so we clear KPIs there and CurrentProductionRow falls back to client-side
  // latest daily rate (active-wells / uptime then read "—").
  useEffect(() => {
    if (assetId == null) {
      setKpis(null);
      return;
    }
    getAssetKpis(
      assetId,
      (data) => setKpis(data),
      () => setKpis(null)
    );
  }, [assetId]);

  const assetsData = [
    {
      title: "Cum Oil",
      icon: "/static/images/assetview/v-icon-01.svg",
      color: "rgba(109, 77, 237, 0.1)",
      dataSeries: historicalData,
      getDates: (dataSeries) => {
        return dataSeries["oil_prd_rate"].map((item) => {
          return item.date;
        });
      },
      computeFunc: (dataSeries, startDate, endDate) => {
        return computeAcummulativeData(
          dataSeries["oil_prd_rate"],
          startDate,
          endDate
        );
      },
      // AV-A6: sparkline = rate-vs-time so the decline is visible. The cumulative
      // curve is monotone-increasing and masks decline, so we plot the monthly
      // rate instead. The card VALUE stays cumulative (computeFunc unchanged).
      computeCurve: (dataSeries, startDate, endDate) => {
        return rateSparkline(dataSeries["oil_prd_rate"], startDate, endDate);
      },
      percentLabel: "本期产量 vs 上期产量",
    },
    {
      title: "Cum Gas",
      icon: "/static/images/assetview/v-icon-02.svg",
      color: "rgba(255, 184, 0, 0.1)",
      dataSeries: historicalData,
      getDates: (dataSeries) => {
        return dataSeries["oil_prd_rate"].map((item) => {
          return item.date;
        });
      },
      computeFunc: (dataSeries, startDate, endDate) => {
        return computeAcummulativeData(
          dataSeries["gas_prd_rate"],
          startDate,
          endDate
        );
      },
      // AV-A6: rate-vs-time sparkline (decline visible).
      computeCurve: (dataSeries, startDate, endDate) => {
        return rateSparkline(dataSeries["gas_prd_rate"], startDate, endDate);
      },
      percentLabel: "本期产量 vs 上期产量",
    },
    {
      title: "Cum Water",
      icon: "/static/images/assetview/v-icon-03.svg",
      color: "rgba(69, 130, 249, 0.1)",
      dataSeries: historicalData,
      getDates: (dataSeries) => {
        return dataSeries["oil_prd_rate"].map((item) => {
          return item.date;
        });
      },
      computeFunc: (dataSeries, startDate, endDate) => {
        return computeAcummulativeData(
          dataSeries["water_prd_rate"],
          startDate,
          endDate
        );
      },
      // AV-A6: rate-vs-time sparkline (water cut/decline visible).
      computeCurve: (dataSeries, startDate, endDate) => {
        return rateSparkline(dataSeries["water_prd_rate"], startDate, endDate);
      },
      percentLabel: "本期产量 vs 上期产量",
    },
    {
      title: "Period Gas/Oil Ratio",
      icon: "/static/images/assetview/v-icon-04.svg",
      color: "rgba(0, 188, 139, 0.1)",
      dataSeries: historicalData,
      getDates: (dataSeries) => {
        return dataSeries["oil_prd_rate"].map((item) => {
          return item.date;
        });
      },
      // AV-A4: GOR = qg/qo. gas series is Mscf so multiply by MSCF_TO_SCF to
      // report scf/bbl (industry standard). Divide-by-zero robust (echoes A1):
      // oil=0 with gas>0 -> Infinity (pure gas), oil=0 & gas=0 -> 0.
      unit: "scf/bbl",
      computeFunc: (dataSeries, startDate, endDate) => {
        let gasRate = computeAcummulativeData(
          dataSeries["gas_prd_rate"],
          startDate,
          endDate
        );
        let oilRate = computeAcummulativeData(
          dataSeries["oil_prd_rate"],
          startDate,
          endDate
        );
        if (oilRate === 0) {
          return gasRate > 0 ? Infinity : 0;
        }
        return (gasRate / oilRate) * MSCF_TO_SCF;
      },
      computeCurve: (dataSeries, startDate, endDate) => {
        let gasDataCurve = computeNormalCurve(
          dataSeries["gas_prd_rate"],
          startDate,
          endDate
        );
        let oilDataCurve = computeNormalCurve(
          dataSeries["oil_prd_rate"],
          startDate,
          endDate
        );

        let curveData = [];
        for (let i = 0; i < oilDataCurve.length; i++) {
          let value = 0;
          let oilRate = oilDataCurve[i].value;
          let gasRate = gasDataCurve[i].value;
          if (oilRate !== 0) {
            value = (gasRate / oilRate) * MSCF_TO_SCF;
          }
          curveData.push([oilDataCurve[i].date, value]);
        }
        return curveData;
      },
      valueFormatter: (v) => {
        if (v === Infinity) return "∞";
        if (v == null || !isFinite(v)) return "—";
        return commarize(Math.round(v));
      },
      percentLabel: "本期 vs 上期",
    },
    {
      title: "Avg Water Cut",
      icon: "/static/images/assetview/v-icon-05.svg",
      color: "rgba(36, 210, 253, 0.1)",
      dataSeries: historicalData,
      getDates: (dataSeries) => {
        return dataSeries["oil_prd_rate"].map((item) => {
          return item.date;
        });
      },
      // AV-A4: Water Cut WC = qw/(qw+qo), displayed as a percentage.
      unit: "%",
      computeFunc: (dataSeries, startDate, endDate) => {
        let waterRate = computeAcummulativeData(
          dataSeries["water_prd_rate"],
          startDate,
          endDate
        );
        let oilRate = computeAcummulativeData(
          dataSeries["oil_prd_rate"],
          startDate,
          endDate
        );
        let baseRate = waterRate + oilRate;
        if (baseRate === 0) {
          return 0;
        }
        return (waterRate / baseRate) * 100;
      },

      computeCurve: (dataSeries, startDate, endDate) => {
        let waterDataCurve = computeNormalCurve(
          dataSeries["water_prd_rate"],
          startDate,
          endDate
        );
        let oilDataCurve = computeNormalCurve(
          dataSeries["oil_prd_rate"],
          startDate,
          endDate
        );

        let curveData = [];
        for (let i = 0; i < oilDataCurve.length; i++) {
          let value = 0;
          let oilRate = oilDataCurve[i].value;
          let waterRate = waterDataCurve[i].value;
          let base = oilRate + waterRate;
          if (base !== 0) {
            value = (waterRate / base) * 100;
          }
          curveData.push([oilDataCurve[i].date, value]);
        }
        return curveData;
      },
      valueFormatter: (v) => {
        if (v == null || !isFinite(v)) return "—";
        return v.toFixed(1);
      },
      percentLabel: "本期 vs 上期",
    },
    {
      title: "Power Consumption",
      icon: "/static/images/assetview/v-icon-06.svg",
      color: "rgba(151, 5, 255, 0.1)",
      dataSeries: historicalData,
      getDates: (dataSeries) => {
        return dataSeries["oil_prd_rate"].map((item) => {
          return item.date;
        });
      },
      computeFunc: (dataSeries, startDate, endDate) => {
        let data = dataSeries["power_consumption"];
        if (data == null) {
          return 0;
        }
        return computeAcummulativeData(data, startDate, endDate);
      },
      // AV-A6: rate-vs-time sparkline.
      computeCurve: (dataSeries, startDate, endDate) => {
        return rateSparkline(dataSeries["power_consumption"], startDate, endDate);
      },
      percentLabel: "本期 vs 上期",
    },
    {
      // AV-A4: BOE = oil + gas/6 (6 Mscf = 1 bbl). Cumulative barrels of oil
      // equivalent over the window; sparkline shows BOE rate-vs-time (decline).
      title: "Cum BOE",
      icon: "/static/images/assetview/v-icon-01.svg",
      color: "rgba(0, 188, 139, 0.1)",
      dataSeries: historicalData,
      unit: "boe",
      getDates: (dataSeries) => {
        return dataSeries["oil_prd_rate"].map((item) => {
          return item.date;
        });
      },
      computeFunc: (dataSeries, startDate, endDate) => {
        let oilVol = computeAcummulativeData(
          dataSeries["oil_prd_rate"],
          startDate,
          endDate
        );
        let gasVol = computeAcummulativeData(
          dataSeries["gas_prd_rate"],
          startDate,
          endDate
        );
        return oilVol + gasVol / MSCF_PER_BOE;
      },
      computeCurve: (dataSeries, startDate, endDate) => {
        let oilCurve = computeNormalCurve(
          dataSeries["oil_prd_rate"],
          startDate,
          endDate
        );
        let gasCurve = computeNormalCurve(
          dataSeries["gas_prd_rate"],
          startDate,
          endDate
        );
        let curveData = [];
        for (let i = 0; i < oilCurve.length; i++) {
          let oilRate = oilCurve[i].value || 0;
          let gasRate = (gasCurve[i] && gasCurve[i].value) || 0;
          curveData.push([oilCurve[i].date, oilRate + gasRate / MSCF_PER_BOE]);
        }
        return curveData;
      },
      valueFormatter: (v) => {
        if (v == null || !isFinite(v)) return "—";
        return commarize(Math.round(v));
      },
      percentLabel: "本期产量 vs 上期产量",
    },
  ];

  let generateCards = (data) => {
    return data.map((item, index) => {
      // Prefer a card's own `unit` (AV-A4 sets scf/bbl, %, boe); fall back to
      // the title-based lookup for the cards that don't override it.
      let unit =
        item.unit != null
          ? item.unit
          : ViewersProductOptions.find((op) => op.name === item.title)?.unit ||
            "";
      return <SummaryCard key={index} {...item} unit={unit} />;
    });
  };

  let cards = generateCards(assetsData);

  return (
    <div className="cards-container">
      <div className="summary-content overflow-visible">
        {/* AV-A5: current-production first screen (latest daily rate / active
            wells / uptime). Engineers want "what is it doing now", not only Cum. */}
        <CurrentProductionRow dataSeries={historicalData} kpis={kpis} />
        <div className="row mt-2 g-0">
          <div className="col pe-1">{cards[0]}</div>
          <div className="col ps-1 pe-1">{cards[1]}</div>
          <div className="col ps-1">{cards[2]}</div>
        </div>
        <div className="row mt-2 g-0">
          <div className="col pe-1">{cards[3]}</div>
          <div className="col ps-1 pe-1">{cards[4]}</div>
          <div className="col ps-1">{cards[6]}</div>
        </div>
        <div className="row mt-2 g-0">
          <div className="col pe-1">{cards[5]}</div>
          <div className="col ps-1 pe-1" />
          <div className="col ps-1" />
        </div>
      </div>
    </div>
  );
}

export { AssetSummary };
